/**
 * Payslip (bulletin de paie) — read-only consultation entity synced from
 * Homère Classic. Field names mirror the source `bulletin` table columns.
 */

import type { YesNoCode } from '../../lib/boolCode';

/** `N` = payslip (bulletin), `O` = final settlement (solde de tout compte / STC). */
export type PayslipSettlement = YesNoCode;

/** Closure status: '' = open, 'P' = provisional, 'M' = closed (monthly close). */
export type PayslipClosure = '' | 'P' | 'M';

export interface Payslip {
  /** bulletin.hc_uuid_key — payslip UUID key */
  hc_uuid_key: string;
  /** bulletin.hc_uuid_key_contrat — contract UUID key */
  hc_uuid_key_contrat: string;
  /** bulletin.mois — payroll month label */
  mois: string;
  /** bulletin.soldetout_compte — N = Payslip, O = STC */
  soldetout_compte: PayslipSettlement;
  /** bulletin.montant — net to pay (null when the payslip is in error) */
  montant: number | null;
  /** bulletin.devise — currency code shown next to the amount (e.g. FCFA, EUR) */
  devise: string | null;
  /** bulletin.date_debut */
  date_debut: string;
  /** bulletin.date_fin */
  date_fin: string;
  /** bulletin.paymentdate — null when not yet paid */
  paymentdate: string | null;
  /** bulletin.codeterrain — owning terrain (scope key) */
  codeterrain: string;
  /** terrain.libelle for codeterrain */
  terrain_libelle: string | null;
  /** bulletin.origin_field_bulletin — origin field code */
  origin_field_bulletin: string;
  /** terrain.libelle for origin_field_bulletin — origin field label */
  origin_field_bulletin_libelle: string | null;
  /** bulletin.statut — '' | 'P' | 'M' */
  statut: PayslipClosure;
}

/** Closure code → i18n key under `payments:payslips.closureStatus`. */
export const PAYSLIP_CLOSURE_KEY: Record<PayslipClosure, 'open' | 'provisional' | 'closed'> = {
  '': 'open',
  P: 'provisional',
  M: 'closed',
};

/**
 * Frozen payslip header shown in the detail — 1:1 projection of `v_payslip_header`.
 * Superset of the list {@link Payslip} with the identity fields
 * (`code_staff`/`nom`/`prenom`) plus the function/level, the analytique codes with
 * their resolved labels and the contract/seniority dates. Values are the frozen
 * `bulletin` copy — never the employee's current record.
 */
export interface PayslipDetailHeader extends Payslip {
  /** bulletin.code_staff — file number (n° dossier) */
  code_staff: string | null;
  /** bulletin.nom — frozen last name */
  nom: string | null;
  /** bulletin.prenom — frozen first name */
  prenom: string | null;
  /** bulletin.libfonction — function label (frozen) */
  libfonction: string | null;
  /** bulletin.niveau — level (frozen) */
  niveau: string | null;
  /** bulletin.codeanalytique — analytical project code (origin-terrain scoped) */
  codeanalytique: string | null;
  /** projet.libelle for codeanalytique — analytical project label */
  lib_projet: string | null;
  /** bulletin.axeanalytique — analytical axis code (origin-terrain scoped) */
  axeanalytique: string | null;
  /** axeanalytique.libelle for axeanalytique — analytical axis label */
  lib_axeanalytique: string | null;
  /** bulletin.dateentree — entry date (frozen) */
  dateentree: string | null;
  /** bulletin.datedeb — contract start date (frozen) */
  datedeb: string | null;
  /** bulletin.anciennete — seniority in months (frozen) */
  anciennete: number | null;
}

/**
 * One payslip line — read-only projection of `v_payslip_lines` (rubriquebulletin
 * ⋈ rubriqueparam). Category flags are raw `'O'`/`'N'`; category grouping,
 * colorization and the VARDAT base selection are derived from these raw fields.
 */
export interface PayslipLine {
  /** rubriquebulletin.hc_uuid_key — line UUID key */
  hc_uuid_key: string;
  /** rubriquebulletin.codeterrain — owning terrain (scope key) */
  codeterrain: string;
  /** rubriqueparam.code — rubrique technical code */
  code: string | null;
  /** rubriqueparam.libelle — rubrique label */
  libelle: string | null;
  /** rubriqueparam.type — `'VARDAT'` means the base is `base_date_bulletin` */
  type: string | null;
  /** rubriquebulletin.base_bulletin — numeric base */
  base_bulletin: number | null;
  /** rubriquebulletin.base_date_bulletin — date base (used when type = VARDAT) */
  base_date_bulletin: string | null;
  /** rubriquebulletin.montant — line amount */
  montant: number | null;
  /** rubriqueparam.numeroordre — pay-plan display order */
  numeroordre: number | null;
  /** rubriqueparam.gain — `'O'` → Gains (green) */
  gain: string | null;
  /** rubriqueparam.retenue — `'O'` → employee charges (red) */
  retenue: string | null;
  /** rubriqueparam.retenuepatronale — `'O'` → employer charges (orange) */
  retenuepatronale: string | null;
  /** rubriqueparam.soldetoutcompte — `'O'` → STC rubrique (blue) */
  soldetoutcompte: string | null;
  /** rubriqueparam.visible — `'O'` visible by default */
  visible: string | null;
  /** rubriqueparam.commentaire — public parameter note (tooltip, read-only) */
  commentaire: string | null;
  /** rubriquebulletin.actif_bulletin — `'O'` when the line is active for this bulletin */
  actif_bulletin: string | null;
}

/** Read-only payslip detail: frozen header + ordered rubrique lines. */
export interface PayslipDetail {
  header: PayslipDetailHeader;
  lines: PayslipLine[];
}
